/**
 * Contracts for module "workspace". Owner: partners.
 * Agency client management, white-label (brand tokens, custom domains, true billing descriptor, hide
 * branding) and agency LLM connections. Pure helpers here are shared with the portal (live preview).
 * Import as `@qm/contracts/workspace`.
 */
import { z } from 'zod';

// ---------------------------------------------------------------- clients (QM-B01-*)
export const CLIENT_STATUSES = ['onboarding', 'active', 'paused', 'archived'] as const;
export const ClientStatusSchema = z.enum(CLIENT_STATUSES);
export const HealthBandSchema = z.enum(['healthy', 'watch', 'at_risk']);
export type HealthBand = z.infer<typeof HealthBandSchema>;

export const ClientListQuerySchema = z.object({
  tab: z.enum(['all', 'onboarding', 'active', 'paused', 'archived']).default('all'),
  health: z.enum(['healthy', 'watch', 'at_risk']).optional(),
  wa: z.enum(['connected', 'disconnected', 'not_connected', 'error']).optional(),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().max(200).optional(),
});

export const ClientCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  ownerEmail: z.string().trim().toLowerCase().email().max(254),
  locale: z.enum(['en', 'ar']).default('en'),
});
export const ClientPauseSchema = z.object({ reason: z.string().trim().min(5).max(500) });
export const ClientAccessUpdateSchema = z.object({ userIds: z.array(z.string().min(1).max(64)).max(200) });

export const CLIENT_DETAIL_TABS = ['summary', 'people', 'services', 'access', 'whatsapp', 'audit'] as const;
export const ClientDetailTabSchema = z.enum(CLIENT_DETAIL_TABS);

export const MetricsUpsertSchema = z.object({
  tenantId: z.string().min(1).max(64),
  leadCount: z.number().int().min(0).optional(),
  deliverablesDone: z.number().int().min(0).optional(),
  deliverablesTotal: z.number().int().min(0).optional(),
  nextReportAt: z.string().datetime({ offset: true }).nullable().optional(),
  waStatus: z.enum(['connected', 'disconnected', 'not_connected', 'error']).optional(),
  lastActivityAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export interface HealthInput {
  leadCount: number; deliverablesDone: number; deliverablesTotal: number; waStatus: string;
  lastActivityAt: string | null; nextReportAt: string | null;
}
export interface HealthResult { score: number; band: HealthBand; reasons: string[] }

/**
 * Client health (QM-B01-001): 100 minus penalties, each with a reason key (`workspace.health.<reason>`).
 * Pure so the list query can filter on a stored band and the portal can explain it.
 */
export function computeHealth(m: HealthInput, now: Date): HealthResult {
  let score = 100;
  const reasons: string[] = [];
  const completion = m.deliverablesTotal > 0 ? m.deliverablesDone / m.deliverablesTotal : 1;
  if (completion < 0.5) { score -= 30; reasons.push('deliverables_behind'); } else if (completion < 0.8) { score -= 15; reasons.push('deliverables_slipping'); }
  if (m.waStatus === 'disconnected' || m.waStatus === 'error') { score -= 25; reasons.push('whatsapp_disconnected'); }
  const idleDays = m.lastActivityAt ? (now.getTime() - Date.parse(m.lastActivityAt)) / 86_400_000 : Infinity;
  if (idleDays > 14) { score -= 25; reasons.push('no_recent_activity'); } else if (idleDays > 7) { score -= 10; reasons.push('low_activity'); }
  if (m.leadCount === 0) { score -= 10; reasons.push('no_leads'); }
  if (m.nextReportAt && Date.parse(m.nextReportAt) < now.getTime()) { score -= 10; reasons.push('report_overdue'); }
  score = Math.max(0, Math.min(100, score));
  return { score, band: score >= 75 ? 'healthy' : score >= 50 ? 'watch' : 'at_risk', reasons };
}

// ---------------------------------------------------------------- white-label (QM-B08-*, QM-NC01-005)
const HEX = /^#[0-9a-fA-F]{6}$/;
export const QUIXO_TRUE_DESCRIPTOR = 'QUIXO MARKETING';
// Editorial Brand System 2026: black text instead of navy
export const NAVY = '#000000' as const;

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}
export function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}
export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}
export interface AccentCheck { ok: boolean; ratio: number; onAccent: '#FFFFFF' | typeof NAVY; suggestion: string | null }
/**
 * WCAG 2.2 AA for the accent (used as CTA fill with text on top): at least 4.5:1 with white or black text.
 * On failure, suggests the nearest darker shade that passes with white text.
 */
export function checkAccent(accent: string): AccentCheck {
  if (!HEX.test(accent)) return { ok: false, ratio: 0, onAccent: '#FFFFFF', suggestion: null };
  const white = contrastRatio(accent, '#FFFFFF');
  const black = contrastRatio(accent, '#000000');
  const best: { ratio: number; onAccent: '#FFFFFF' | '#000000' } = white >= black ? { ratio: white, onAccent: '#FFFFFF' } : { ratio: black, onAccent: '#000000' };
  if (best.ratio >= 4.5) return { ok: true, ...best, suggestion: null };
  let n = parseInt(accent.slice(1), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  let hex = accent;
  for (let i = 0; i < 100 && contrastRatio(hex, '#FFFFFF') < 4.5; i++) {
    r = Math.floor(r * 0.95); g = Math.floor(g * 0.95); b = Math.floor(b * 0.95);
    n = (r << 16) | (g << 8) | b;
    hex = `#${n.toString(16).padStart(6, '0').toUpperCase()}`;
  }
  return { ok: false, ...best, suggestion: hex };
}

/** Theme tokens for the client portal (one shell, one theme key: only brand variables are overridden). */
export function brandTokens(accent: string | null | undefined): Record<string, string> {
  if (!accent || !checkAccent(accent).ok) return {};
  const { onAccent } = checkAccent(accent);
  return { '--q-hue': accent.toUpperCase(), '--q-hue-deep': accent.toUpperCase(), '--q-on-hue': onAccent };
}

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '$': 's', '@': 'a', '!': 'i', '|': 'i' };
/** Normalise a brand string for impersonation checks (case, accents, leetspeak, separators). */
export function normaliseBrand(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().split('').map((ch) => LEET[ch] ?? ch).join('').replace(/[^a-z]/g, '');
}
/** Agency branding may never present itself as Quixo (NFR C01-05). */
export function impersonatesQuixo(s: string | null | undefined): boolean {
  if (!s) return false;
  const n = normaliseBrand(s);
  return n.includes('quixo') || n.includes('quix0') || n.includes('qulxo') || n.includes('kwixo');
}

export interface DescriptorProfile { billingModel: 'quixo_billed' | 'agency_billed' | null; legalName: string | null; descriptor: string | null }
/**
 * The true billing descriptor (NFR C01-05): the legal seller of record, never the agency's display brand.
 * Quixo-billed (or not yet contracted) -> QUIXO MARKETING; agency-billed -> the agency's own validated descriptor.
 */
export function trueBillingDescriptor(p: DescriptorProfile | null): string {
  if (!p || p.billingModel !== 'agency_billed') return QUIXO_TRUE_DESCRIPTOR;
  const d = (p.descriptor ?? p.legalName ?? '').toUpperCase().replace(/[^A-Z0-9 .*-]/g, '').trim().slice(0, 22);
  return d.length >= 5 ? d : QUIXO_TRUE_DESCRIPTOR;
}
export function descriptorProblem(descriptor: string): string | null {
  if (impersonatesQuixo(descriptor)) return 'impersonation';
  if (!/^[A-Za-z0-9 .*-]{5,22}$/.test(descriptor) || /^[0-9 .*-]+$/.test(descriptor)) return 'invalid_descriptor';
  return null;
}

export const BRAND_SURFACES = ['portal', 'report', 'email', 'invoice', 'payment_page'] as const;
export type BrandSurface = (typeof BRAND_SURFACES)[number];
/** Surface templates. Every one must carry {{descriptor}} and {{legal_footer}} (checked in CI by the render test). */
export const SURFACE_TEMPLATES: Record<BrandSurface, string> = {
  portal: '{{portal_name}}|{{logo}}|{{powered_by}}|{{legal_footer}}|{{descriptor}}',
  report: '{{logo}}|{{portal_name}} report|{{legal_footer}}|Billed by {{descriptor}}',
  email: '{{logo}}|{{portal_name}}|{{support_email}}|{{legal_footer}}|Billed by {{descriptor}}',
  invoice: 'Invoice|Seller: {{descriptor}}|{{legal_footer}}',
  payment_page: 'Pay|Charged by {{descriptor}}|{{legal_footer}}',
};
export function templateProblems(template: string): string[] {
  const out: string[] = [];
  if (!template.includes('{{descriptor}}')) out.push('descriptor_missing');
  if (!template.includes('{{legal_footer}}')) out.push('legal_footer_missing');
  return out;
}

export const BrandSettingsSchema = z.object({
  portalName: z.string().trim().min(2).max(60).optional(),
  logoUrl: z.string().trim().url().max(500).refine((u) => u.startsWith('https://'), 'logo must be https').optional(),
  accentColor: z.string().regex(HEX, 'accent must be #RRGGBB').optional(),
  supportEmail: z.string().trim().toLowerCase().email().optional(),
  hideQuixoBranding: z.boolean().optional(),
});
export type BrandSettingsInput = z.infer<typeof BrandSettingsSchema>;

// ---------------------------------------------------------------- domains (QM-B08-002)
export const DomainCreateSchema = z.object({
  hostname: z.string().trim().toLowerCase().max(253).regex(/^(?!-)([a-z0-9-]{1,63}\.){2,}[a-z]{2,63}$/, 'use a subdomain such as portal.myagency.com'),
});
export const CNAME_TARGET = 'clients.quixomarketing.com';
export const TXT_PREFIX = '_quixo-challenge';
export const SSL_ACTIVATION_SLO_MS = 15 * 60 * 1000;

// ---------------------------------------------------------------- BYO LLM (QM-B07-003)
/** Providers/models an agency may bring its own key for. Mirrors the platform allowlist owned by the ai workstream. */
export const AGENCY_LLM_ALLOWLIST: Record<string, readonly string[]> = {
  anthropic: ['claude-sonnet-4-5', 'claude-haiku-4-5', 'claude-opus-4-5'],
  'workers-ai': ['@cf/meta/llama-3.3-70b-instruct-fp8-fast'],
};
export const LlmConnectionCreateSchema = z.object({
  provider: z.string().trim().min(2).max(40),
  model: z.string().trim().min(2).max(120),
  apiKey: z.string().min(16).max(400),
});

export const WORKSPACE_EVENTS = {
  clientCreated: 'workspace.client.created',
  clientPaused: 'workspace.client.paused',
  clientResumed: 'workspace.client.resumed',
  domainActivated: 'workspace.domain.activated',
  sslSloBreached: 'workspace.domain.ssl_slo_breached',
} as const;
